import { Download, FileBarChart, Package, Printer } from "lucide-react";
import { MATERIAL_CATEGORIES } from "..";

const CategoryReportsSection = ({ ctx }) => {
  const {
    exportToExcel,
    lowStockCount,
    materials,
    stockSummary,
    totalInventoryValue
  } = ctx;
  return (
    <>
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">
              <FileBarChart className="w-5 h-5 inline mr-2 text-primary" />
              Category-wise Stock Reports
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={exportToExcel}
                className="btn-primary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export to Excel</span>
              </button>
              <button
                onClick={() => window.print()}
                className="btn-outline flex items-center space-x-2"
              >
                <Printer className="w-4 h-4" />
                <span>Print Report</span>
              </button>
            </div>
          </div>

          {/* Category Summary Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {MATERIAL_CATEGORIES.map((cat) => {
              const categoryItems = stockSummary.filter(s => s.category === cat.id || (!s.category && cat.id === "construction"));
              const totalValue = categoryItems.reduce((sum, s) => sum + (s.stock_value || 0), 0);
              const lowStockCount = categoryItems.filter(s => s.low_stock_alert).length;
              const avgUtilization = categoryItems.length > 0 
                ? categoryItems.reduce((sum, s) => sum + ((s.total_outward || 0) / ((s.opening_stock || 0) + (s.total_inward || 1)) * 100), 0) / categoryItems.length
                : 0;
              
              return (
                <div key={cat.id} className="stat-card border-t-4" style={{borderTopColor: cat.color === 'primary' ? '#1e3a5f' : cat.color === 'accent' ? '#f59e0b' : cat.color === 'warning' ? '#ef4444' : '#64748b'}}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">{cat.label}</p>
                      <p className="font-heading font-bold text-2xl text-primary mt-1">{categoryItems.length}</p>
                      <p className="text-xs text-slate-500">Items</p>
                    </div>
                    <Package className="w-8 h-8 text-slate-300" />
                  </div>
                  <div className="space-y-2 pt-4 border-t border-slate-200">
                    <div className="flex justify-between">
                      <span className="text-xs text-slate-600">Total Value</span>
                      <span className="text-sm font-mono font-bold text-primary">₹{totalValue.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-slate-600">Low Stock</span>
                      <span className={`text-sm font-bold ${lowStockCount > 0 ? 'text-error' : 'text-success'}`}>{lowStockCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-slate-600">Avg Utilization</span>
                      <span className="text-sm font-mono">{avgUtilization.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detailed Category Reports */}
          {MATERIAL_CATEGORIES.map((cat) => {
            const categoryItems = stockSummary.filter(s => s.category === cat.id || (!s.category && cat.id === "construction"));
            if (categoryItems.length === 0) return null;
            
            const totalValue = categoryItems.reduce((sum, s) => sum + (s.stock_value || 0), 0);
            const totalInward = categoryItems.reduce((sum, s) => sum + (s.total_inward || 0), 0);
            const totalOutward = categoryItems.reduce((sum, s) => sum + (s.total_outward || 0), 0);
            
            return (
              <div key={cat.id} className="stat-card mb-6">
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
                  <h4 className="font-heading font-bold text-lg text-slate-800">{cat.label}</h4>
                  <div className="flex space-x-4 text-sm">
                    <span className="text-slate-600">Total Value: <span className="font-bold text-primary">₹{totalValue.toLocaleString('en-IN')}</span></span>
                    <span className="text-success">In: {totalInward.toFixed(2)}</span>
                    <span className="text-warning">Out: {totalOutward.toFixed(2)}</span>
                  </div>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Material</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Unit</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Opening</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Inward</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Outward</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Current</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Rate</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Value</th>
                      <th className="text-center py-2 px-3 text-xs uppercase tracking-widest text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryItems.map((item) => (
                      <tr key={item.id} className={`border-b border-slate-100 ${item.low_stock_alert ? 'bg-error/5' : ''}`}>
                        <td className="py-2 px-3 font-medium">{item.name}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm">{item.unit}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm">{item.opening_stock?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm text-success">+{item.total_inward?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm text-warning">-{item.total_outward?.toFixed(2)}</td>
                        <td className={`py-2 px-3 text-right font-mono text-sm font-bold ${item.low_stock_alert ? 'text-error' : 'text-primary'}`}>
                          {item.current_stock?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-sm">₹{item.rate?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right font-mono text-sm font-bold">₹{item.stock_value?.toLocaleString('en-IN')}</td>
                        <td className="py-2 px-3 text-center">
                          {item.low_stock_alert ? (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-error/20 text-error">Low</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-success/20 text-success">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="py-2 px-3 font-bold" colSpan="7">Category Total</td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-primary">₹{totalValue.toLocaleString('en-IN')}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })}

          {/* Grand Total */}
          <div className="stat-card bg-primary/5 border-2 border-primary">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Grand Total Inventory Value</p>
                <p className="font-heading font-black text-4xl text-primary font-mono">₹{totalInventoryValue.toLocaleString('en-IN')}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Total Materials: <span className="font-bold text-primary">{materials.length}</span></p>
                <p className="text-sm text-slate-600">Low Stock Items: <span className="font-bold text-error">{lowStockCount}</span></p>
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default CategoryReportsSection;
